import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PrimaryTitleGray } from '../../../app/theme/tokens/colors';
import { Spinner } from '../../../shared/ui/Spinners';
import { TitledPanel } from '../../../shared/ui/dashboard/titledPanel/titledPanel';
import { WideLayout } from '../../../shared/layout/widelayout';
import { useLayoutStore } from '../../../stores/portalStore';
import { useRechargeStore } from '../store/rechargeStore';
import { AutoCompleteRechargePatients } from '../components/autocomplete';
import { ListPatientsRecharge } from '../components/listPatientsRecharge';
import { useResponsive } from '../../../hooks/useResponsive';
import {
  UNREGISTERED_PATIENT_MESSAGE,
  isUnregisteredPortalHomecarePatient,
} from '../../../services/patients/patientsService';
import { OrderFlowPageHeader } from '../../../shared/ui/orderFlowPageHeader';

const MainPanels = () => {
  const reset = useRechargeStore((state) => state.reset);
  const fetchPatients = useRechargeStore((state) => state.fetchPatients);
  const patientsLoading = useRechargeStore((state) => state.patientsLoading);
  const patients = useRechargeStore((state) => state.patients);
  const patientInfoMessage = useRechargeStore(
    (state) => state.patientInfoMessage
  );
  const rows = useRechargeStore((state) => state.rows);
  const selectedPatient = useRechargeStore((state) => state.selectedPatient);
  const onlyWithCylinders = useRechargeStore(
    (state) => state.onlyWithCylinders
  );
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const contentWidth = useLayoutStore((state) => state.contentWidth);
  const { isDesktop } = useResponsive();
  const containerWidth = isDesktop && contentWidth ? contentWidth : '100%';
  const [containerHeight, setContainerHeight] = useState(520);
  const pageRootRef = useRef(null);
  const previousOnlyWithCylindersRef = useRef(onlyWithCylinders);
  const isInactiveSelectedPatient =
    String(
      selectedPatient?.status ??
        selectedPatient?.patitent_status ??
        selectedPatient?.patient_status ??
        ''
    )
      .trim()
      .toLowerCase() === 'inativo';
  const isUnregisteredSelectedPatient =
    isUnregisteredPortalHomecarePatient(selectedPatient);

  const recalcAvailableHeight = () => {
    if (!pageRootRef.current) return;
    const top = pageRootRef.current.getBoundingClientRect().top;
    const outerBottomMargin = 16;
    const available = window.innerHeight - top - outerBottomMargin;
    setContainerHeight(Math.max(320, Math.floor(available)));
  };

  useEffect(() => {
    const id = requestAnimationFrame(recalcAvailableHeight);
    window.addEventListener('resize', recalcAvailableHeight);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', recalcAvailableHeight);
    };
  }, [rows, patients.length, isLoading, patientsLoading, selectedPatient]);

  useEffect(() => {
    const keepState = searchParams.get('keepState');
    if (!keepState) {
      reset();
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadPatients = async () => {
      const shouldKeepState = searchParams.get('keepState') === 'true';
      const onlyWithCylindersChanged =
        previousOnlyWithCylindersRef.current !== onlyWithCylinders;
      previousOnlyWithCylindersRef.current = onlyWithCylinders;

      if (
        shouldKeepState &&
        !onlyWithCylindersChanged &&
        useRechargeStore.getState().patients.length > 0
      ) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        await fetchPatients(
          shouldKeepState && !onlyWithCylindersChanged ? undefined : null
        );
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadPatients();

    return () => {
      isActive = false;
    };
  }, [onlyWithCylinders]);

  const actionFilter = () => {
    return (
      <>
        <span
          style={{
            fontSize: '14px',
            marginRight: '5px',
            color: PrimaryTitleGray,
          }}
        ></span>
      </>
    );
  };

  return (
    <div
      ref={pageRootRef}
      style={{
        maxWidth: containerWidth,
        margin: '15px',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '16px',
        height: `${containerHeight}px`,
        overflow: 'hidden',
      }}
    >
      <div
        className="recharge-title"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
        }}
      >
        <OrderFlowPageHeader
          title="Recarga"
          subtitle={
            selectedPatient?.name
              ? `Solicite recarga ou troca de cilindros para ${selectedPatient.name}.`
              : ''
          }
        />
        <div className="recharge-input" style={{ width: '50%' }}>
          <AutoCompleteRechargePatients
            loading={isLoading || patientsLoading}
          ></AutoCompleteRechargePatients>
        </div>
      </div>
      <TitledPanel
        style={{ overflowY: 'hidden', flex: 1, minHeight: 0 }}
        outlet={
          <>
            {isLoading || patientsLoading ? (
              <div
                style={{
                  width: '100%',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Spinner></Spinner>
              </div>
            ) : (
              <div
                style={{
                  height: '100%',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {Array.isArray(patients) && patients.length === 0 ? (
                  <div
                    style={{
                      width: '100%',
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px 12px',
                      color: PrimaryTitleGray,
                      boxSizing: 'border-box',
                      textAlign: 'center',
                    }}
                  >
                    {patientInfoMessage
                      ? patientInfoMessage
                      : isUnregisteredSelectedPatient
                      ? UNREGISTERED_PATIENT_MESSAGE
                      : isInactiveSelectedPatient
                      ? 'Paciente inativo. Não é possível solicitar recarga para este paciente.'
                      : selectedPatient
                      ? 'Nenhuma informação encontrada para o paciente selecionado.'
                      : 'Nenhum paciente encontrado.'}
                  </div>
                ) : (
                  <ListPatientsRecharge></ListPatientsRecharge>
                )}
              </div>
            )}
          </>
        }
        action={actionFilter()}
      ></TitledPanel>
    </div>
  );
};

export const RechargeSelectPatientsPage = () => {
  return <WideLayout outlet={<MainPanels />}></WideLayout>;
};
